import React from 'react';
import { graphql } from 'gatsby';
//@ts-ignore
import * as styles from './product-specifications.module.css';
//@ts-ignore
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import { useState } from 'react';
import {
    CTAConfiguration,
    KontentStringValue,
    CTALocation,
    KontentProductImage,
} from '../../../types/KontentProps';
import loadable from '@loadable/component';

import Materials, { Material } from './Materials';
const ProductRender = loadable(() => import('./ProductRender'));
const ProductInteriorRender = loadable(() => import('./ProductInteriorPhoto'));
import ArrowLgButton from '../Global/ArrowLgButton';
import ProductSpecsList, { Specifications } from './ProductSpecsList';
import ProductSpecsLinks from './ProductSpecsLinks';
import { LazyMotion, domAnimation, m, AnimatePresence } from 'framer-motion';
import parse from 'html-react-parser';
import ProductSpecificationsV2 from '../ProductSpecificationsV2';
import {
    optimizeKontentImageUrl,
    buildKontentSrcSet,
    imagePreset,
} from '../Global/DataUtils';

type GalleryImages = {
    elements: {
        file?: {
            value: {
                url: string;
            };
        };
        untitled_asset?: {
            value: {
                url: string;
            };
        };
    };
    internal?: {
        type?:
            | 'kontent_item_media___product_image'
            | 'kontent_item_media___360_interior_render'
            | 'kontent_item_media___360_render';
    };
};
interface epigraphContent {
    element: {
        cta_configuration__cta_label?: KontentStringValue;
        cta_configuration__cta_location?: CTALocation;
        cta_configuration__cta_window_action?: {
            value: {
                codename: 'same_window' | 'new_window';
            }[];
        };
        cta_copy: KontentStringValue;
        cta_icons: KontentProductImage;
        sku: KontentStringValue;
        ua_code: KontentStringValue;
    };
}
export interface epigraphCTA {
    cta_configuration__cta_label?: KontentStringValue;
    cta_configuration__cta_location?: CTALocation;
    cta_configuration__cta_window_action?: {
        value: {
            codename: 'same_window' | 'new_window';
        }[];
    };
    cta_copy: KontentStringValue;
    cta_icons: KontentProductImage;
    sku: KontentStringValue;
    ua_code: KontentStringValue;
}
interface Props extends CTAConfiguration {
    ctas: CTAConfiguration;
    gallery_images: { value: GalleryImages[] };
    materials: { value: Material[] };
    specs: Specifications;
    component_anchor_name: {
        value: string;
    };
    disclaimer: {
        value: string;
    };
    qr_codes?: { value: epigraphContent };
    kontentItemId?: string;
}

const ProductSpecifications: React.FC<Props> = ({
    ctas,
    gallery_images,
    materials,
    specs,
    kontentItemId,
    ...props
}) => {
    const material = materials.value;
    const CAROUSEL_ARROW_COLOR = '#505251';
    const breakpoints = useBreakpoint();
    const carouselItems = gallery_images.value;
    const [currentCarouselItem, setCurrentCarouselItem] = useState(0);
    const [showModal, setShowModal] = useState(false);
    const epigraphCTAValue = props?.qr_codes?.value[0];
    const anchorValue = props?.component_anchor_name?.value;
    const disclaimer = props?.disclaimer?.value;
    const isVersionTwo =
        props?.product_specs_version?.value[0]?.codename === 'version_2';

    const renderCarouselItems = (isModal = false) => {
        return carouselItems.map((item: GalleryImages, idx: any) => {
            const initial = { opacity: 0 };
            const animate = { opacity: 1 };
            const exit: any = { opacity: 0 };

            if (!isModal) {
                exit.position = 'absolute';
            }

            if (idx !== currentCarouselItem % carouselItems.length) {
                return null;
            }

            if (item.internal?.type === 'kontent_item_media___product_image') {
                return (
                    <m.div
                        className={!isModal ? styles.motion : styles.imgWrap}
                        initial={initial}
                        animate={animate}
                        exit={exit}
                        key={item?.elements?.file?.value[0]?.url + idx}
                    >
                        <div data-kontent-element-codename="gallery_images">
                            <img
                                src={optimizeKontentImageUrl(
                                    item?.elements?.file?.value[0]?.url,
                                    {
                                        ...imagePreset.fullWidth.desktop,
                                        q: 95,
                                    }
                                )}
                                srcSet={buildKontentSrcSet(
                                    item?.elements?.file?.value[0]?.url,
                                    imagePreset.fullWidth.desktop.w,
                                    { fm: 'webp' as const, q: 95 }
                                )}
                                sizes="(max-width: 768px) 100vw, 50vw"
                                alt={
                                    item?.elements?.file?.value[0]
                                        ?.description || 'Product image'
                                }
                                loading="lazy"
                                decoding="async"
                                onClick={
                                    !isModal
                                        ? () => handleClickZoom(item)
                                        : undefined
                                }
                            />
                        </div>
                        <button
                            className={`${styles.zoom} ${
                                isModal ? styles.zoomModal : ''
                            }`}
                            onClick={
                                !isModal
                                    ? () => handleClickZoom(item)
                                    : undefined
                            }
                        ></button>
                    </m.div>
                );
            }

            if (item.internal?.type === 'kontent_item_media___360_render') {
                return (
                    <m.div
                        initial={initial}
                        animate={animate}
                        exit={exit}
                        key={
                            item?.elements?.untitled_asset?.value[0]?.url + idx
                        }
                    >
                        <ProductRender
                            url={item?.elements?.untitled_asset?.value[0]?.url}
                        />
                    </m.div>
                );
            }

            if (
                item.internal?.type ===
                'kontent_item_media___360_interior_render'
            ) {
                return (
                    <m.div
                        initial={initial}
                        animate={animate}
                        exit={exit}
                        key={
                            item?.elements?.untitled_asset?.value[0]?.url + idx
                        }
                    >
                        <ProductInteriorRender
                            url={item?.elements?.untitled_asset?.value[0]?.url}
                        />
                    </m.div>
                );
            }

            return null;
        });
    };

    const handleClickPrev = () => {
        if (currentCarouselItem === 0) {
            setCurrentCarouselItem(carouselItems.length - 1);
        } else {
            setCurrentCarouselItem(currentCarouselItem - 1);
        }
    };

    const handleClickNext = () => {
        setCurrentCarouselItem(currentCarouselItem + 1);
    };

    const handleClickZoom = (image: any) => {
        let imagePosition = gallery_images.value.indexOf(image);
        setCurrentCarouselItem(imagePosition);
        setShowModal(true);
    };

    return (
        <>
            {isVersionTwo ? (
                <ProductSpecificationsV2
                    ctas={ctas}
                    gallery_images={gallery_images}
                    materials={materials}
                    specs={specs}
                    {...props}
                />
            ) : (
                <>
                    <section
                        className={styles.section}
                        id={anchorValue || undefined}
                        data-kontent-item-id={kontentItemId}
                    >
                        <div className={styles.imagesAndActions}>
                            <div>
                                <LazyMotion features={domAnimation}>
                                    <AnimatePresence>
                                        {renderCarouselItems()}
                                    </AnimatePresence>
                                </LazyMotion>
                                {carouselItems.length > 1 && (
                                    <div className={styles.carouselControls}>
                                        <ArrowLgButton
                                            strokeColor={CAROUSEL_ARROW_COLOR}
                                            direction="left"
                                            handleClick={handleClickPrev}
                                        />

                                        <ArrowLgButton
                                            strokeColor={CAROUSEL_ARROW_COLOR}
                                            direction="right"
                                            handleClick={handleClickNext}
                                        />
                                    </div>
                                )}
                            </div>
                            {breakpoints.md && (
                                <ProductSpecsLinks
                                    documents={ctas.value}
                                    epigraphCTA={epigraphCTAValue?.elements}
                                />
                            )}
                        </div>
                        <div className={styles.specs}>
                            <h2>Product Specifications</h2>
                            <ProductSpecsList specifications={specs} />
                            <Materials materials={material} />
                            {!breakpoints.md && (
                                <>
                                    {disclaimer && (
                                        <div className={styles.disclaimer}>
                                            {parse(disclaimer)}
                                        </div>
                                    )}
                                    <ProductSpecsLinks
                                        documents={ctas.value}
                                        epigraphCTA={epigraphCTAValue?.elements}
                                    />
                                </>
                            )}
                        </div>

                        {showModal === true && breakpoints.md && (
                            <div className={styles.background}>
                                <div className={styles.modal}>
                                    <div
                                        className={styles.closeBtn}
                                        onClick={() => setShowModal(false)}
                                    ></div>
                                    <LazyMotion features={domAnimation}>
                                        {renderCarouselItems(true)}
                                    </LazyMotion>
                                    {carouselItems.length > 1 && (
                                        <div
                                            className={
                                                styles.carouselControlsModal
                                            }
                                        >
                                            <ArrowLgButton
                                                strokeColor={
                                                    CAROUSEL_ARROW_COLOR
                                                }
                                                direction="left"
                                                handleClick={handleClickPrev}
                                            />
                                            <ArrowLgButton
                                                strokeColor={
                                                    CAROUSEL_ARROW_COLOR
                                                }
                                                direction="right"
                                                handleClick={handleClickNext}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </section>
                    {breakpoints.md && disclaimer && (
                        <div className={styles.disclaimer}>
                            {parse(disclaimer)}
                        </div>
                    )}
                </>
            )}
        </>
    );
};

export default ProductSpecifications;

export const fragmentProductSpecifications = graphql`
    fragment ProductSpecifications on kontent_item_component___product_specifications {
        elements {
            component_anchor_name {
                value
            }
            disclaimer {
                value
            }
            ctas {
                name
                type
                value {
                    id
                    ... on kontent_item_content_item___cta {
                        id
                        elements {
                            cta_label {
                                value
                            }
                            cta_location {
                                value {
                                    ... on kontent_item_layout___master_page {
                                        id
                                        elements {
                                            slug {
                                                value
                                                url
                                            }
                                        }
                                    }
                                    ... on kontent_item_documents___products {
                                        id
                                        elements {
                                            file {
                                                value {
                                                    url
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            cta_location_anchor_point {
                                value
                            }
                            cta_window_action {
                                value {
                                    codename
                                }
                            }
                        }
                    }
                }
            }
            contact_panel {
                value {
                    ... on kontent_item_content_item___specs_contact_panel {
                        elements {
                            headline {
                                value
                            }
                            description {
                                ...kontentRichText
                            }
                            cta_configuration__cta_location_anchor_point {
                                value
                            }
                            cta_configuration__cta_window_action {
                                value {
                                    codename
                                }
                            }
                            cta_configuration__cta_location {
                                value {
                                    ... on kontent_item_layout___master_page {
                                        id
                                        elements {
                                            slug {
                                                value
                                                url
                                            }
                                        }
                                    }
                                }
                            }
                            cta_configuration__cta_label {
                                value
                            }
                            image {
                                ...kontentImage
                            }
                        }
                    }
                }
            }
            gallery_images {
                value {
                    ... on kontent_item_media___image {
                        elements {
                            caption {
                                value
                            }
                            file {
                                value {
                                    url
                                    description
                                    name
                                }
                            }
                        }
                    }
                    ... on kontent_item_media___product_image {
                        id
                        elements {
                            caption {
                                value
                            }
                            file {
                                value {
                                    url
                                    description
                                    name
                                }
                            }
                        }
                        internal {
                            type
                        }
                    }
                    ... on kontent_item_media___360_render {
                        elements {
                            untitled_asset {
                                value {
                                    url
                                    type
                                }
                            }
                        }
                        internal {
                            type
                        }
                    }
                    ... on kontent_item_media___360_interior_render {
                        elements {
                            untitled_asset {
                                value {
                                    url
                                }
                            }
                        }
                        internal {
                            type
                        }
                    }
                }
            }
            materials {
                value {
                    ... on kontent_item_data___material {
                        id
                        elements {
                            image {
                                value {
                                    ... on kontent_item_media___image {
                                        id
                                        elements {
                                            file {
                                                value {
                                                    url
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            material {
                                taxonomy_group
                                value {
                                    name
                                }
                            }
                            material_feature {
                                taxonomy_group
                                value {
                                    name
                                }
                            }
                            material_feature___german {
                                taxonomy_group
                                value {
                                    name
                                }
                            }
                            name_for_display {
                                value
                            }
                        }
                        internal {
                            type
                        }
                    }
                }
            }
            headline {
                value
            }
            product_name {
                value
            }
            schema_org_product_name {
                value
            }
            schema_org_description {
                value
            }
            schema_org_offer_price {
                value
            }
            schema_org_price_currency {
                value
            }
            schema_org_offer_availability {
                value {
                    codename
                    name
                }
            }
            schema_org_add_schema {
                value {
                    codename
                    name
                }
            }
            schema_org_product_image {
                value {
                    url
                    name
                    description
                }
            }
            product_specs_version {
                value {
                    codename
                    name
                }
            }
            qr_codes {
                value {
                    ... on kontent_item_component___qr_code {
                        id
                        elements {
                            cta_configuration__cta_label {
                                value
                            }
                            cta_configuration__cta_location {
                                value {
                                    ... on kontent_item_layout___master_page {
                                        id
                                        elements {
                                            slug {
                                                value
                                                url
                                            }
                                        }
                                    }
                                }
                            }
                            cta_configuration__cta_window_action {
                                value {
                                    codename
                                }
                            }
                            cta_copy {
                                value
                            }
                            sku {
                                value
                            }
                            ua_code {
                                value
                            }
                            cta_icons {
                                value {
                                    ... on kontent_item_media___image {
                                        elements {
                                            file {
                                                value {
                                                    url
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
            specs {
                value {
                    ... on kontent_item_data___sauna_specifications {
                        elements {
                            capacity {
                                value {
                                    ... on kontent_item_data___capacity {
                                        elements {
                                            capacity_value {
                                                value
                                            }
                                            capacity_tag {
                                                value {
                                                    codename
                                                }
                                            }
                                            capacity_tag___de_de {
                                                value {
                                                    codename
                                                }
                                            }
                                            icon {
                                                value {
                                                    ... on kontent_item_media___icon {
                                                        id
                                                        elements {
                                                            icon_svg_file {
                                                                value {
                                                                    url
                                                                }
                                                            }
                                                            color {
                                                                value {
                                                                    codename
                                                                }
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            category_1_headline {
                                value
                            }
                            category_1_icon {
                                value {
                                    ... on kontent_item_media___icon {
                                        elements {
                                            icon_svg_file {
                                                value {
                                                    url
                                                }
                                            }
                                            color {
                                                value {
                                                    codename
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            category_1_measurements {
                                value {
                                    ... on kontent_item_data___open_measurement {
                                        id
                                        elements {
                                            measurement_content {
                                                ...kontentRichText
                                            }
                                        }
                                    }
                                    ... on kontent_item_data___material_weight {
                                        id
                                        elements {
                                            weight {
                                                value
                                            }
                                        }
                                    }
                                    ... on kontent_item_data___spec_measurement {
                                        id
                                        elements {
                                            value {
                                                value
                                            }
                                            title {
                                                value
                                            }
                                            small_sub_copy {
                                                value
                                            }
                                        }
                                    }
                                }
                            }
                            category_2_headline {
                                value
                            }
                            category_2_icon {
                                value {
                                    ... on kontent_item_media___icon {
                                        elements {
                                            icon_svg_file {
                                                value {
                                                    url
                                                }
                                            }
                                            color {
                                                value {
                                                    codename
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            category_2_measurements {
                                value {
                                    ... on kontent_item_data___open_measurement {
                                        id
                                        elements {
                                            measurement_content {
                                                ...kontentRichText
                                            }
                                        }
                                    }
                                    ... on kontent_item_data___material_weight {
                                        id
                                        elements {
                                            weight {
                                                value
                                            }
                                            material {
                                                value {
                                                    ... on kontent_item_data___material {
                                                        elements {
                                                            name_for_display {
                                                                value
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                    ... on kontent_item_data___spec_measurement {
                                        id
                                        elements {
                                            value {
                                                value
                                            }
                                            title {
                                                value
                                            }
                                            small_sub_copy {
                                                value
                                            }
                                        }
                                    }
                                }
                            }
                            category_3_headline {
                                value
                            }
                            category_3_icon {
                                value {
                                    ... on kontent_item_media___icon {
                                        elements {
                                            icon_svg_file {
                                                value {
                                                    url
                                                }
                                            }
                                            color {
                                                value {
                                                    codename
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            category_3_measurements {
                                value {
                                    ... on kontent_item_data___open_measurement {
                                        id
                                        elements {
                                            measurement_content {
                                                ...kontentRichText
                                            }
                                        }
                                    }
                                    ... on kontent_item_data___material_weight {
                                        id
                                        elements {
                                            weight {
                                                value
                                            }
                                        }
                                    }
                                    ... on kontent_item_data___spec_measurement {
                                        id
                                        elements {
                                            value {
                                                value
                                            }
                                            title {
                                                value
                                            }
                                            small_sub_copy {
                                                value
                                            }
                                        }
                                    }
                                }
                            }
                            category_4_headline {
                                value
                            }
                            category_4_icon {
                                value {
                                    ... on kontent_item_media___icon {
                                        elements {
                                            icon_svg_file {
                                                value {
                                                    url
                                                }
                                            }
                                            color {
                                                value {
                                                    codename
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            category_4_measurements {
                                value {
                                    ... on kontent_item_data___open_measurement {
                                        id
                                        elements {
                                            measurement_content {
                                                ...kontentRichText
                                            }
                                        }
                                    }
                                    ... on kontent_item_data___material_weight {
                                        id
                                        elements {
                                            weight {
                                                value
                                            }
                                        }
                                    }
                                    ... on kontent_item_data___spec_measurement {
                                        id
                                        elements {
                                            value {
                                                value
                                            }
                                            title {
                                                value
                                            }
                                            small_sub_copy {
                                                value
                                            }
                                        }
                                    }
                                }
                            }
                            category_5_headline {
                                value
                            }
                            category_5_icon {
                                value {
                                    ... on kontent_item_media___icon {
                                        elements {
                                            icon_svg_file {
                                                value {
                                                    url
                                                }
                                            }
                                            color {
                                                value {
                                                    codename
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            category_5_measurements {
                                value {
                                    ... on kontent_item_data___open_measurement {
                                        id
                                        elements {
                                            measurement_content {
                                                ...kontentRichText
                                            }
                                        }
                                    }
                                    ... on kontent_item_data___material_weight {
                                        id
                                        elements {
                                            weight {
                                                value
                                            }
                                        }
                                    }
                                    ... on kontent_item_data___spec_measurement {
                                        id
                                        elements {
                                            value {
                                                value
                                            }
                                            title {
                                                value
                                            }
                                            small_sub_copy {
                                                value
                                            }
                                        }
                                    }
                                }
                            }
                            category_6_headline {
                                value
                            }
                            category_6_icon {
                                value {
                                    ... on kontent_item_media___icon {
                                        elements {
                                            icon_svg_file {
                                                value {
                                                    url
                                                }
                                            }
                                            color {
                                                value {
                                                    codename
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            category_6_measurements {
                                value {
                                    ... on kontent_item_data___open_measurement {
                                        id
                                        elements {
                                            measurement_content {
                                                ...kontentRichText
                                            }
                                        }
                                    }
                                    ... on kontent_item_data___material_weight {
                                        id
                                        elements {
                                            weight {
                                                value
                                            }
                                        }
                                    }
                                    ... on kontent_item_data___spec_measurement {
                                        id
                                        elements {
                                            value {
                                                value
                                            }
                                            title {
                                                value
                                            }
                                            small_sub_copy {
                                                value
                                            }
                                        }
                                    }
                                }
                            }
                            category_7_headline {
                                value
                            }
                            category_7_icon {
                                value {
                                    ... on kontent_item_media___icon {
                                        elements {
                                            icon_svg_file {
                                                value {
                                                    url
                                                }
                                            }
                                            color {
                                                value {
                                                    codename
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            wood_types {
                                value {
                                    ... on kontent_item_data___material {
                                        id
                                        elements {
                                            image {
                                                value {
                                                    ... on kontent_item_media___image {
                                                        id
                                                        elements {
                                                            file {
                                                                value {
                                                                    url
                                                                }
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                            material {
                                                taxonomy_group
                                                value {
                                                    name
                                                }
                                            }
                                            material_feature {
                                                taxonomy_group
                                                value {
                                                    name
                                                }
                                            }
                                            material_feature___german {
                                                taxonomy_group
                                                value {
                                                    name
                                                }
                                            }
                                            name_for_display {
                                                value
                                            }
                                        }
                                        internal {
                                            type
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`;
